// src/pages/DiscoverIndustryPage.jsx
//
// SEO-first per-industry catalog pages. One URL per supported industry
// (/discover/dental, /discover/real-estate, /discover/hvac, ...) — each is
// a real, content-rich, indexable page that ranks for "best AI tools for
// [industry]"-class queries.
//
// Why this is a moat-building page:
//   - The query "best AI tools for dentists" is high-intent and currently
//     dominated by Zapier/G2/listicle blogs that aren't industry-specific
//     and aren't tied to a personalized matching engine.
//   - Each page here cites the same scored matching engine that powers the
//     paid $149 blueprint. Visitors who like what they see funnel into the
//     audit form (with utm_source=discover_seo so attribution is captured).
//   - Every Visit click routes through /api/launch/{toolId} for affiliate
//     tracking — organic traffic now monetizes from buyer #1.
//   - Each new tool added to tools.json automatically deepens these pages
//     without manual content work.
//
// SEO essentials baked in:
//   - Per-industry <title>, <meta description>, canonical, OG tags via Helmet
//   - JSON-LD ItemList schema for rich-snippet eligibility
//   - Real intro content (template-generated using actual benchmark data)
//   - Methodology section (E-E-A-T trust signal: Expertise, Experience,
//     Authoritativeness, Trustworthiness)
//   - Internal linking back to /discover hub + outward to /audit
//   - Sitemap registration for these URLs (frontend/public/sitemap.xml)

import React, { useEffect, useMemo, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
  ArrowRight, ExternalLink, ShieldCheck, Sparkles, TrendingUp,
  AlertCircle, Loader2, Zap,
} from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';

const API_BASE =
  process.env.REACT_APP_BACKEND_URL ||
  'https://ai-workshop-llc-backend.onrender.com';

// Canonical site URL — matches sitemap.xml + the apex->www redirect we ship.
const SITE_URL = 'https://www.aiworkshopllc.com';

// ── Industry metadata ──────────────────────────────────────────────────────
// Maps URL slugs (kebab-case in the URL like real-estate) to backend-canonical
// slugs (snake_case like real_estate) and human display labels. Also drives
// the per-industry intro copy + value-prop angles. Adding a new industry =
// add an entry here + add the URL to sitemap.xml.
const INDUSTRY_META = {
  'real-estate':           { slug: 'real_estate',           label: 'Real Estate',            audienceShort: 'real estate agents and brokerages', topPain: 'Slow lead response (38-hour industry average)', avgImpact: '$8K–42K/year' },
  'dental':                { slug: 'dental',                label: 'Dental Practices',       audienceShort: 'dental practice owners and front-desk teams', topPain: 'No-shows costing $400–800/day', avgImpact: '$12K–58K/year' },
  'hvac':                  { slug: 'hvac',                  label: 'HVAC & Home Services',   audienceShort: 'HVAC and home-services contractors', topPain: 'Missed calls after hours = lost work', avgImpact: '$10K–41K/year' },
  'legal':                 { slug: 'legal',                 label: 'Legal',                  audienceShort: 'solo attorneys and small law firms', topPain: 'Document review consuming billable hours', avgImpact: '$15K–67K/year' },
  'accounting':            { slug: 'accounting',            label: 'Accounting',             audienceShort: 'CPAs, bookkeepers, and tax preparers', topPain: 'Manual data entry during tax season', avgImpact: '$8K–35K/year' },
  'healthcare':            { slug: 'healthcare',            label: 'Healthcare',             audienceShort: 'medical and behavioral-health practices', topPain: 'Insurance verification and patient intake', avgImpact: '$15K–52K/year' },
  'restaurants':           { slug: 'hospitality',           label: 'Restaurants & Hospitality', audienceShort: 'restaurant owners and managers', topPain: 'Reservation gaps and labor scheduling', avgImpact: '$8K–38K/year' },
  'retail':                { slug: 'retail',                label: 'Retail',                 audienceShort: 'physical retail store owners', topPain: 'Foot-traffic conversion and inventory', avgImpact: '$10K–40K/year' },
  'ecommerce':             { slug: 'ecommerce',             label: 'E-commerce',             audienceShort: 'online store owners and DTC brands', topPain: 'Cart abandonment and product copy at scale', avgImpact: '$12K–48K/year' },
  'professional-services': { slug: 'professional_services', label: 'Professional Services',  audienceShort: 'consulting and agency owners', topPain: 'Manual proposals and lead qualification', avgImpact: '$10K–45K/year' },
  'technology':            { slug: 'technology',            label: 'Technology',             audienceShort: 'software and IT-services teams', topPain: 'Documentation drift and customer support load', avgImpact: '$15K–60K/year' },
  'finance':               { slug: 'finance',               label: 'Finance',                audienceShort: 'financial advisors and small fintechs', topPain: 'Compliance review and client communications', avgImpact: '$15K–55K/year' },
  'manufacturing':         { slug: 'manufacturing',         label: 'Manufacturing',          audienceShort: 'small manufacturers and fabricators', topPain: 'Quote generation and inventory forecasting', avgImpact: '$12K–50K/year' },
  'education':             { slug: 'education',             label: 'Education',              audienceShort: 'schools and education businesses', topPain: 'Grading load and parent communications', avgImpact: '$8K–32K/year' },
  'construction':          { slug: 'construction',          label: 'Construction',           audienceShort: 'contractors and construction firms', topPain: 'Bid generation and project tracking', avgImpact: '$10K–45K/year' },
  'auto-repair':           { slug: 'auto_repair',           label: 'Auto Repair',            audienceShort: 'auto repair and body shops', topPain: 'Customer intake and parts ordering', avgImpact: '$8K–35K/year' },
  'med-spa':               { slug: 'med_spa',               label: 'Med Spa & Aesthetics',   audienceShort: 'med spa and aesthetic practice owners', topPain: 'Appointment booking and consult follow-up', avgImpact: '$12K–48K/year' },
  'saas':                  { slug: 'saas',                  label: 'SaaS',                   audienceShort: 'B2B SaaS founders and growth teams', topPain: 'Onboarding and churn-prediction workflows', avgImpact: '$15K–60K/year' },
};

// Pricing-band styling — used by tool cards.
const PRICING_BAND_STYLE = {
  free:       { label: 'Free tier',      cls: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30' },
  budget:     { label: 'Under $50/mo',   cls: 'bg-blue-500/15 text-blue-300 border-blue-500/30' },
  mid:        { label: '$50–$200/mo',    cls: 'bg-amber-500/15 text-amber-300 border-amber-500/30' },
  enterprise: { label: '$200+/mo',       cls: 'bg-purple-500/15 text-purple-300 border-purple-500/30' },
};

// Build affiliate-tracked launch URL. Same pattern as ToolsTab — every Visit
// click routes through /api/launch/{toolId} for click logging + affiliate
// attribution. No reportId here (organic / Discover traffic) but UTM still
// captured server-side from query string when present.
const buildLaunchUrl = (toolId, fallbackUrl) => {
  if (!toolId) return fallbackUrl || null;
  return `${API_BASE}/api/launch/${encodeURIComponent(toolId)}?utm_source=discover_seo`;
};

export default function DiscoverIndustryPage() {
  const { industry: urlSlug } = useParams();
  const navigate = useNavigate();
  const meta = INDUSTRY_META[(urlSlug || '').toLowerCase()];

  const [tools, setTools] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // Live catalog count via /api/tools/stats with 351 fallback (same pattern
  // as R21.1's loader + R26.7's preview badge — auto-corrects as catalog
  // grows so the SEO copy doesn't drift to "348+ tools" while the rest
  // of the funnel says 351+).
  const [liveToolCount, setLiveToolCount] = useState(351);
  useEffect(() => {
    fetch(`${process.env.REACT_APP_BACKEND_URL || 'https://ai-workshop-llc-backend.onrender.com'}/api/tools/stats`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (d?.total) setLiveToolCount(d.total); })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!meta) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(
          `${API_BASE}/api/tools/catalog?industry=${encodeURIComponent(meta.slug)}&limit=20`,
        );
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        if (!cancelled) {
          setTools(Array.isArray(data?.tools) ? data.tools : []);
          setLoading(false);
        }
      } catch (e) {
        if (!cancelled) {
          setError(e.message || 'Failed to load tool catalog');
          setLoading(false);
        }
      }
    })();
    return () => { cancelled = true; };
  }, [meta]);

  // JSON-LD ItemList schema — this is what gets Google to render rich
  // snippets ("10 ranked items") in search results. Rebuilt as tools load.
  const jsonLdSchema = useMemo(() => {
    if (!meta) return null;
    return {
      '@context': 'https://schema.org',
      '@type': 'ItemList',
      name: `Best AI Tools for ${meta.label}`,
      description: `${tools.length} AI tools scored by AI Workshop's matching engine for ${meta.audienceShort}.`,
      numberOfItems: tools.length,
      itemListElement: tools.slice(0, 10).map((t, i) => ({
        '@type': 'ListItem',
        position: i + 1,
        item: {
          '@type': 'SoftwareApplication',
          name: t.name,
          applicationCategory: t.category || 'BusinessApplication',
          description: t.why || `${t.name} for ${meta.audienceShort}.`,
          ...(t.website_url ? { url: t.website_url } : {}),
        },
      })),
    };
  }, [meta, tools]);

  // Unknown industry slug — show a soft 404 with links back to the hub.
  // Better UX than React Router's NotFound for someone who arrived from
  // a stale link (also better SEO — soft-404s hurt less than hard-404s).
  if (!meta) {
    return (
      <div className="min-h-screen bg-[#0c1324] text-white">
        <Helmet>
          <title>Industry Not Found — AI Workshop</title>
          <meta name="robots" content="noindex" />
        </Helmet>
        <Header />
        <main className="max-w-3xl mx-auto px-6 py-24 text-center">
          <AlertCircle className="w-12 h-12 text-amber-400 mx-auto mb-4" />
          <h1 className="text-2xl font-semibold mb-3">We don't have a page for "{urlSlug}" yet</h1>
          <p className="text-slate-400 mb-6">Browse our full catalog or pick from our covered industries below.</p>
          <div className="flex flex-wrap gap-2 justify-center">
            {Object.entries(INDUSTRY_META).map(([url, m]) => (
              <Link key={url} to={`/discover/${url}`}
                className="text-sm bg-slate-800/60 hover:bg-slate-700/60 text-slate-200 px-4 py-2 rounded-full border border-white/10">
                {m.label}
              </Link>
            ))}
          </div>
          <div className="mt-10">
            <Link to="/discover" className="text-blue-400 hover:underline inline-flex items-center gap-1.5">
              ← Back to full catalog
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  // ── Real page ───────────────────────────────────────────────────────────
  const pageTitle = `Best AI Tools for ${meta.label} (2026) — Scored by AI Workshop`;
  const pageDescription = `Curated AI tool stack for ${meta.audienceShort} — ${tools.length || '20+'} tools ranked by our 7-dimension matching engine (industry fit, problem coverage, goal alignment, pricing, integration, ease, ROI evidence). Pricing, integration complexity, and tracked vendor links. Free to browse.`;
  const canonicalUrl = `${SITE_URL}/discover/${urlSlug.toLowerCase()}`;

  return (
    <div className="min-h-screen bg-[#0c1324] text-white">
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDescription} />
        <link rel="canonical" href={canonicalUrl} />
        {/* OG / Twitter cards — for shareable previews when this URL is
            posted to social or messaging apps. */}
        <meta property="og:title"       content={pageTitle} />
        <meta property="og:description" content={pageDescription} />
        <meta property="og:type"        content="website" />
        <meta property="og:url"         content={canonicalUrl} />
        <meta property="og:site_name"   content="AI Workshop" />
        <meta name="twitter:card"        content="summary_large_image" />
        <meta name="twitter:title"       content={pageTitle} />
        <meta name="twitter:description" content={pageDescription} />
        {/* JSON-LD ItemList — rich snippet eligibility for Google. Cached
            client-side; Google's modern crawler renders JS so it sees this. */}
        {jsonLdSchema && (
          <script type="application/ld+json">
            {JSON.stringify(jsonLdSchema)}
          </script>
        )}
      </Helmet>

      <Header />

      <main className="max-w-5xl mx-auto px-6 py-12 sm:py-16">
        {/* Breadcrumb */}
        <nav className="text-xs text-slate-500 mb-6">
          <Link to="/" className="hover:text-slate-300">Home</Link>
          <span className="mx-2">/</span>
          <Link to="/discover" className="hover:text-slate-300">Discover</Link>
          <span className="mx-2">/</span>
          <span className="text-slate-300">{meta.label}</span>
        </nav>

        {/* Hero */}
        <header className="mb-12">
          <div className="inline-flex items-center gap-2 text-xs uppercase tracking-wider text-blue-400 bg-blue-500/10 border border-blue-500/20 px-3 py-1 rounded-full mb-5">
            <Sparkles className="w-3.5 h-3.5" /> Updated 2026 · Scored by our matching engine
          </div>
          <h1 className="text-4xl sm:text-5xl font-bold leading-tight mb-5">
            Best AI Tools for {meta.label}
          </h1>
          <p className="text-lg text-slate-300 leading-relaxed mb-6 max-w-3xl">
            We score {liveToolCount}+ AI tools across 7 dimensions — industry fit, problem coverage, goal alignment, pricing, integration complexity, ease of adoption, and ROI evidence. Below: the tools that score highest for {meta.audienceShort}.
          </p>
          {/* Anchor stat — gives Google + buyers a real data point. */}
          <div className="flex flex-wrap gap-4 text-sm">
            <div className="bg-slate-900/60 border border-white/10 rounded-xl px-4 py-2.5">
              <div className="text-xs text-slate-500">Top pain we solve</div>
              <div className="text-white font-medium">{meta.topPain}</div>
            </div>
            <div className="bg-slate-900/60 border border-white/10 rounded-xl px-4 py-2.5">
              <div className="text-xs text-slate-500">Typical Year 1 impact</div>
              <div className="text-white font-medium">{meta.avgImpact}</div>
            </div>
          </div>
        </header>

        {/* Tool list */}
        {loading && (
          <div className="flex items-center gap-3 text-slate-400 py-12">
            <Loader2 className="w-5 h-5 animate-spin" />
            <span>Loading scored tools…</span>
          </div>
        )}
        {error && !loading && (
          <div className="bg-amber-500/8 border border-amber-500/20 text-amber-200 rounded-xl p-4 mb-6">
            Couldn't load the catalog right now ({error}). Try refreshing, or browse the <Link to="/discover" className="underline">full catalog</Link>.
          </div>
        )}
        {!loading && !error && tools.length === 0 && (
          <div className="bg-slate-900/60 border border-white/10 rounded-xl p-6 text-slate-400 mb-6">
            No tools cataloged for this industry yet. Try the <Link to="/discover" className="text-blue-400 hover:underline">full catalog</Link>, or take the audit and we'll match what we have to your specific problems.
          </div>
        )}

        {!loading && tools.length > 0 && (
          <ol className="space-y-3" aria-label={`Top ${tools.length} AI tools for ${meta.label}`}>
            {tools.map((t, i) => {
              const band = PRICING_BAND_STYLE[(t.pricing_band || '').toLowerCase()] || null;
              const launchUrl = buildLaunchUrl(t.id, t.affiliate_url || t.website_url);
              return (
                <Card key={t.id || `${t.name}-${i}`} className="bg-slate-900/70 border border-white/10">
                  <CardContent className="p-5">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex items-start gap-4 min-w-0 flex-1">
                        {/* Rank chip */}
                        <div className="w-10 h-10 rounded-xl bg-blue-500/10 border border-blue-500/30 text-blue-300 font-bold text-sm flex items-center justify-center flex-shrink-0">
                          {i + 1}
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="flex flex-wrap items-baseline gap-2 mb-1">
                            <h2 className="text-lg font-semibold text-white">{t.name}</h2>
                            {t.smb_score && (
                              <Badge className="bg-blue-500/10 text-blue-300 border-blue-500/30 text-[10px]">
                                Score {t.smb_score}/10
                              </Badge>
                            )}
                          </div>
                          {t.category && (
                            <div className="text-xs text-slate-500 mb-2">{t.category}</div>
                          )}
                          {t.why && (
                            <p className="text-sm text-slate-300 leading-relaxed">{t.why}</p>
                          )}
                          <div className="flex flex-wrap gap-2 mt-3">
                            {band && <Badge className={`text-[10px] ${band.cls}`}>{band.label}</Badge>}
                            {t.complexity && (
                              <Badge className="bg-white/5 text-slate-300 border-white/10 text-[10px]">
                                {t.complexity} setup
                              </Badge>
                            )}
                            {t.adoption_trend && (
                              <Badge className="bg-emerald-500/10 text-emerald-300 border-emerald-500/30 text-[10px]">
                                <TrendingUp className="w-2.5 h-2.5 mr-0.5" /> {t.adoption_trend}
                              </Badge>
                            )}
                          </div>
                        </div>
                      </div>
                      {/* Visit — affiliate-tracked redirect */}
                      {launchUrl && (
                        <a
                          href={launchUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full bg-blue-500/15 hover:bg-blue-500/25 text-blue-300 border border-blue-500/30 flex-shrink-0 transition-colors"
                        >
                          Visit <ExternalLink className="w-3 h-3" />
                        </a>
                      )}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </ol>
        )}

        {/* CTA — funnels SEO traffic into the paid audit. UTM tagged so we
            can attribute downstream conversions back to organic search. */}
        <section className="mt-12 bg-gradient-to-br from-blue-500/10 to-purple-500/10 border border-blue-500/30 rounded-2xl p-8 text-center">
          <h2 className="text-2xl font-bold mb-3">Want a stack tuned to YOUR {meta.label.toLowerCase()} business?</h2>
          <p className="text-slate-300 max-w-2xl mx-auto mb-6">
            This list is the catalog. Take our 3-minute audit and we'll cross-reference your specific problems, budget, team size, and existing tools against the same matching engine — then deliver a 20-page implementation blueprint with tools, prompts, ROI projections, and a 12-week roadmap.
          </p>
          <Button
            onClick={() => navigate(`/audit?industry=${meta.slug}&utm_source=discover_seo&utm_medium=organic&utm_campaign=${urlSlug}_seo`)}
            className="bg-gradient-to-br from-blue-500 to-blue-600 text-white px-7 font-semibold rounded-full"
          >
            Get my personalized blueprint <ArrowRight className="w-4 h-4 ml-1.5" />
          </Button>
          <div className="mt-4 text-xs text-slate-500 flex items-center justify-center gap-2">
            <ShieldCheck className="w-3.5 h-3.5" /> $149 · 60-day money-back guarantee
          </div>
        </section>

        {/* Methodology — E-E-A-T trust signal. Tells Google + buyers HOW
            we score, which is the moat: a transparent engine vs an
            opaque listicle. */}
        <section className="mt-12">
          <h2 className="text-lg font-semibold text-white mb-4">How we score these tools</h2>
          <div className="bg-slate-900/40 border border-white/10 rounded-2xl p-5">
            <p className="text-sm text-slate-300 leading-relaxed mb-4">
              Every tool is scored on seven dimensions before it appears on this list:
            </p>
            <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm text-slate-300">
              <li><span className="text-blue-300 font-medium">Industry fit (20pts)</span> — explicit support for {meta.label.toLowerCase()} workflows.</li>
              <li><span className="text-blue-300 font-medium">Problem coverage (35pts)</span> — overlap with the actual pain points {meta.audienceShort} report.</li>
              <li><span className="text-blue-300 font-medium">Goal alignment (10pts bonus)</span> — addresses what owners are trying to achieve, not just what's broken (e.g. "automate follow-up", "lift retention by X%"). Surfaces tools the problem-only score would miss.</li>
              <li><span className="text-blue-300 font-medium">Pricing (10pts)</span> — fits an SMB AI budget, not enterprise.</li>
              <li><span className="text-blue-300 font-medium">Integration (10pts)</span> — connects to tools businesses already use.</li>
              <li><span className="text-blue-300 font-medium">Ease of adoption (10pts)</span> — minutes to value, not months.</li>
              <li><span className="text-blue-300 font-medium">ROI evidence (15pts)</span> — backed by sample sizes and median lift data, not vendor claims.</li>
            </ul>
            <p className="text-xs text-slate-500 mt-4 leading-relaxed">
              Scores update as we collect outcome data from real implementations. Tools that don't deliver get downweighted; tools that consistently win for {meta.audienceShort} rise. The engine improves every month.
            </p>
          </div>
        </section>

        {/* Internal linking — sister industries. Helps both buyers and
            Google's crawler discover other indexable pages on the site. */}
        <section className="mt-10">
          <h2 className="text-sm font-semibold uppercase tracking-wider text-slate-500 mb-3">Other industries</h2>
          <div className="flex flex-wrap gap-2">
            {Object.entries(INDUSTRY_META)
              .filter(([url]) => url !== urlSlug.toLowerCase())
              .slice(0, 12)
              .map(([url, m]) => (
                <Link
                  key={url}
                  to={`/discover/${url}`}
                  className="text-xs bg-slate-800/60 hover:bg-slate-700/60 text-slate-200 px-3 py-1.5 rounded-full border border-white/10"
                >
                  {m.label}
                </Link>
              ))}
            <Link
              to="/discover"
              className="text-xs bg-blue-500/10 hover:bg-blue-500/20 text-blue-300 px-3 py-1.5 rounded-full border border-blue-500/30"
            >
              Full catalog →
            </Link>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
